import { FormResponseCard } from '../../../../shared/src/application-forms/FormResponseCard';
import { ApplicationNextStep } from '../../components/applications/ApplicationNextStep';
import { normalizeApplicationStatus, applicationStatusLabel } from '../../../../shared/src/utils/applicationStatus';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, FileDown, CheckCircle, XCircle, Clock, Building, Mail, Phone, MapPin, Calendar, GraduationCap, Award, Briefcase, Download, Eye, Save, Pencil } from 'lucide-react';
import { agentApi } from '@/services/agentApi';
import { AgentStudentApplication } from '@/types/user';
import { useSettings } from '@/contexts/SettingsContext';
import toast from 'react-hot-toast';
import { DocumentRequestList } from '../../components/document-requests/DocumentRequestList';
import { StudentSummary } from '../../components/applications/StudentSummary';
import './applicationDetails.css';

export const ApplicationViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <ApplicationViewContent key={id} id={id} />;
};

const ApplicationViewContent: React.FC<{ id?: string }> = ({ id }) => {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [application, setApplication] = useState<AgentStudentApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [previewingDocument, setPreviewingDocument] = useState<any | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [agentResponse, setAgentResponse] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const mounted = useRef(false);
  const requestGeneration = useRef(0);

  useEffect(() => () => {
    if (previewUrl?.startsWith('blob:')) window.URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    mounted.current = true;
    if (id) void loadApplication();
    return () => {
      mounted.current = false;
      requestGeneration.current += 1;
    };
  }, [id]);

  const handleSaveAsProfile = async () => {
    if (!application) return;
    
    const confirmMessage = 'Would you like to save this application data as a student profile for future use?';
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setSavingProfile(true);
    try {
      await agentApi.createStudentProfile({
        applicationId: application.id,
      });
      toast.success('Student profile created successfully!');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error?.response?.data?.error || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const loadApplication = async () => {
    if (!id || !mounted.current) return;
    const generation = ++requestGeneration.current;
    setLoading(true);
    try {
      // Support both numeric ID and reference string
      const app = await agentApi.getApplication(id);
      if (!mounted.current || generation !== requestGeneration.current) return;
      setApplication(app);
      
      // Load documents if application exists
      if (app.id) {
        void loadDocuments(app.id, generation);
      }
    } catch (error: any) {
      if (!mounted.current || generation !== requestGeneration.current) return;
      console.error('Error loading application:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to load application';
      toast.error(errorMessage);
      navigate('/applications');
    } finally {
      if (mounted.current && generation === requestGeneration.current) setLoading(false);
    }
  };

  const loadDocuments = async (applicationId: number, generation: number) => {
    setLoadingDocuments(true);
    try {
      const docs = await agentApi.getApplicationDocuments(applicationId);
      if (mounted.current && generation === requestGeneration.current) setDocuments(docs);
    } catch (error: any) {
      if (mounted.current && generation === requestGeneration.current) console.error('Error loading documents:', error);
    } finally {
      if (mounted.current && generation === requestGeneration.current) setLoadingDocuments(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; icon: React.ReactNode; label: string }> = {
      DRAFT: { class: 'bg-secondary', icon: <FileText size={14} />, label: 'Draft' },
      SUBMITTED: { class: 'bg-info', icon: <Clock size={14} />, label: 'Submitted' },
      APPROVED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Approved' },
      VISA_APPLICATION: { class: 'bg-info', icon: <FileText size={14} />, label: 'Visa Application' },
      TUITION_PAYMENT_STAGE: { class: 'bg-info', icon: <FileText size={14} />, label: 'Tuition Payment' },
      PAID: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Paid' },
      VISA_APPROVED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Visa Approved' },
      REJECTED: { class: 'bg-danger', icon: <XCircle size={14} />, label: 'Rejected' },
      DEFERRED: { class: 'bg-info', icon: <Clock size={14} />, label: 'Deferred' },
      WITHDRAWN: { class: 'bg-secondary', icon: <XCircle size={14} />, label: 'Withdrawn' },
      ENROLLED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Enrolled' },
    };

    const badge = badges[normalizeApplicationStatus(status)] || { class: 'bg-secondary', icon: badges.DRAFT.icon };
    const color = badge.class.replace('bg-', '');
    return (
      <span
        className={`badge rounded-pill bg-${color}-subtle text-${color}-emphasis d-inline-flex align-items-center gap-1 px-2 py-1 fw-normal`}
        aria-label={`Application status: ${applicationStatusLabel(status)}`}
      >
        {badge.icon}
        {applicationStatusLabel(status)}
      </span>
    );
  };

  const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return typeof date === 'string' ? date : 'N/A';
    return parsed.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSendMessage = async () => {
    if (!application || !agentResponse.trim()) {
      toast.error('Please enter your message');
      return;
    }

    try {
      setSubmittingResponse(true);
      if (application.requiresResponse) {
        await agentApi.submitResponse(application.id, agentResponse);
        toast.success('Response submitted successfully');
      } else {
        await agentApi.addNote(application.id, agentResponse);
        toast.success('Message sent successfully');
      }
      setAgentResponse('');
      await loadApplication();
    } catch (error: any) {
      console.error('Error sending message:', error);
      toast.error(error?.response?.data?.error || 'Failed to send message');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const handleDownload = async (documentId: number, fileName: string) => {
    if (!application) return;
    
    try {
      const blob = await agentApi.downloadApplicationDocument(application.id, documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading document:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to download document';
      toast.error(errorMessage);
    }
  };

  const handleDownloadPDF = async () => {
    if (!application || !id) return;
    
    try {
      setDownloadingPDF(true);
      const blob = await agentApi.downloadApplicationPDF(id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const universityName = application.universities?.name || 'Kalandra';
      const sanitizedUniversityName = universityName.replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `${sanitizedUniversityName}_Application_${application.reference || application.id}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Application PDF downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to download PDF';
      toast.error(errorMessage);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handlePreview = async (document: any) => {
    if (!application) return;
    
    try {
      const mimeType = document.mimeType || getMimeTypeFromFileName(document.originalName || '');
      let previewUrl: string;
      
      // Use the stored presigned URL from path if available
      if (document.path && (document.path.startsWith('http://') || document.path.startsWith('https://'))) {
        // Use the stored presigned URL directly - it's already in the database
        // Permission check already happened when fetching the file list
        previewUrl = document.path;
        console.log('Using stored URL from path:', previewUrl);
      } else {
        // Fallback: Fetch through API (for legacy files or if URL is missing)
        const blob = await agentApi.downloadApplicationDocument(application.id, document.id);
        if (!mounted.current) return;
        const previewBlob = new Blob([blob], { type: mimeType });
        previewUrl = window.URL.createObjectURL(previewBlob);
        console.log('Fetched document through API (fallback)');
      }
      
      setPreviewUrl(previewUrl);
      setPreviewingDocument(document);
    } catch (error: any) {
      if (!mounted.current) return;
      console.error('Error previewing document:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to preview document';
      toast.error(errorMessage);
    }
  };

  const closePreview = () => {
    // The preview effect releases blob URLs on close, replacement or route exit.
    setPreviewUrl(null);
    setPreviewingDocument(null);
  };

  const isImageFile = (mimeType: string) => {
    return mimeType?.startsWith('image/');
  };

  const isPdfFile = (mimeType: string) => {
    return mimeType === 'application/pdf';
  };

  const getMimeTypeFromFileName = (fileName: string): string => {
    if (!fileName) return 'application/pdf';
    const ext = fileName.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      'pdf': 'application/pdf',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'png': 'image/png',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
    };
    return mimeTypes[ext || ''] || 'application/pdf';
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading application...</span>
              </div>
              <p className="mt-3 text-muted">Loading application details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="alert alert-danger">
              Application not found
            </div>
            <button className="btn btn-primary" onClick={() => navigate('/applications')}>
              Back to Applications
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Parse documents metadata
  let documentsMetadata: Record<string, any> = {};
  if (application.documents) {
    try {
      documentsMetadata = JSON.parse(application.documents);
    } catch (e) {
      console.error('Error parsing documents metadata:', e);
    }
  }

  return (
    <div className="container-fluid py-4 application-detail-page">
      <div className="application-detail-frame">
          {/* Header */}
          <div className="application-detail-header d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <div className="d-flex align-items-center gap-3">
              <button
                className="btn btn-link p-0"
                aria-label="Back to Applications"
                onClick={() => navigate('/applications')}
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="h3 fw-bold text-dark mb-1">Application Details</h1>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <p className="text-muted mb-0">
                    Reference: <code className="text-primary">{application.reference}</code>
                  </p>
                  {getStatusBadge(application.status)}
                </div>
              </div>
            </div>
            <div className="btn-group btn-group-sm flex-shrink-0" role="group" aria-label="Application actions">
              {application.status === 'DRAFT' && (
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigate(`/applications/${application.id}/edit`)}
                  title="Edit Application"
                  aria-label="Edit Application"
                >
                  <Pencil size={16} aria-hidden="true" />
                  <span className="d-none d-md-inline ms-2">Edit</span>
                </button>
              )}
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDownloadPDF}
                disabled={downloadingPDF}
                title={downloadingPDF ? 'Generating PDF...' : 'Download Application PDF'}
                aria-label={downloadingPDF ? 'Generating PDF...' : 'Download Application PDF'}
                aria-busy={downloadingPDF}
              >
                <FileDown size={16} aria-hidden="true" />
                <span className="d-none d-md-inline ms-2">{downloadingPDF ? 'Generating...' : 'Download'}</span>
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleSaveAsProfile}
                disabled={savingProfile}
                title={savingProfile ? 'Saving profile...' : 'Save as Student Profile'}
                aria-label={savingProfile ? 'Saving profile...' : 'Save as Student Profile'}
                aria-busy={savingProfile}
              >
                <Save size={16} aria-hidden="true" />
                <span className="d-none d-md-inline ms-2">{savingProfile ? 'Saving...' : 'Save as'}</span>
              </button>
            </div>
          </div>

          <ApplicationNextStep
            status={application.status}
            variant="detail"
            action={
              <button className="btn btn-sm btn-outline-primary text-nowrap" onClick={() => navigate(`/applications/${application.id}/visa-support`)}>
                Visa Support
              </button>
            }
          />

          {/* Response Required Notifier */}
          {application.requiresResponse && (
            <div className="alert alert-warning d-flex align-items-center mb-4 border-0 shadow-sm">
              <FileText size={20} className="me-2" />
              <div className="flex-grow-1">
                <strong>Response Required:</strong> This application requires a response from you. Please scroll down to provide your response.
              </div>
            </div>
          )}

          <div className="application-detail-workspace">
            <StudentSummary student={application} formatDate={formatDate} />
            <section className="application-detail-content" aria-label="Application details">
          {/* Application Information */}
          <div className="row g-4">
            {application.configuredFormResponse ? (
              <div className="col-12">
                <FormResponseCard id={application.id} load={agentApi.getApplicationFormResponse} download={agentApi.downloadApplicationFormFile} />
              </div>
            ) : (
              <>
            {/* A. Qualification You Are Applying For */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0 fw-semibold">A. Qualification You Are Applying For</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <strong><Building size={18} className="me-2" />University:</strong>
                      <p className="mb-0">{application.universities?.name || 'N/A'}</p>
                      {application.universities?.city && (
                        <small className="text-muted">{application.universities.city}, {application.universities.country}</small>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <strong><GraduationCap size={18} className="me-2" />Program:</strong>
                      <p className="mb-0">{application.university_programs?.programs?.name || 'N/A'}</p>
                      {application.university_programs?.programs?.level && (
                        <small className="text-muted">Level: {application.university_programs.programs.level}</small>
                      )}
                    </div>
                    {application.programmeStrand && (
                      <div className="col-md-6 mb-3">
                        <strong>Programme Strand:</strong>
                        <p className="mb-0">{application.programmeStrand}</p>
                      </div>
                    )}
                    {application.programmeStartDate && (
                      <div className="col-md-6 mb-3">
                        <strong><Calendar size={18} className="me-2" />Programme Start Date:</strong>
                        <p className="mb-0">{formatDate(application.programmeStartDate)}</p>
                      </div>
                    )}
                    {application.programmeEndDate && (
                      <div className="col-md-6 mb-3">
                        <strong><Calendar size={18} className="me-2" />Programme End Date:</strong>
                        <p className="mb-0">{formatDate(application.programmeEndDate)}</p>
                      </div>
                    )}
                    <div className="col-md-6 mb-3">
                      <strong>Studied at Kalandra Before:</strong>
                      <p className="mb-0">{application.studiedAtKalandraBefore ? 'Yes' : 'No'}</p>
                    </div>
                    {application.howDidYouFindOut && (
                      <div className="col-md-6 mb-3">
                        <strong>How did you find out:</strong>
                        <p className="mb-0">{application.howDidYouFindOut}</p>
                      </div>
                    )}
                    {application.howDidYouFindOutWhen && (
                      <div className="col-md-6 mb-3">
                        <strong>When did you study at the institution:</strong>
                        <p className="mb-0">{application.howDidYouFindOutWhen}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* C. Contact Details */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h5 className="mb-0 fw-semibold"><Mail size={18} className="me-2" />C. Contact Details</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {(application.homePhone || application.homeMobile || application.homeFax) && (
                      <div className="col-md-6 mb-3">
                        <strong><Phone size={18} className="me-2" />Home Contact:</strong>
                        <p className="mb-0">
                          {application.homePhone && `Phone: ${application.homePhone}`}
                          {application.homePhone && application.homeMobile && ' | '}
                          {application.homeMobile && `Mobile: ${application.homeMobile}`}
                          {application.homeFax && (application.homePhone || application.homeMobile) && ' | '}
                          {application.homeFax && `Fax: ${application.homeFax}`}
                        </p>
                      </div>
                    )}
                    {(application.homeAddressLine || application.homeCity || application.homeState || application.homeCountry || application.homeZipCode || application.homeAddress) && (
                      <div className="col-12 mb-3">
                        <strong><MapPin size={18} className="me-2" />Home Address:</strong>
                        {application.homeAddressLine || application.homeCity || application.homeState || application.homeCountry || application.homeZipCode ? (
                          <div className="mt-2">
                            {application.homeAddressLine && (
                              <p className="mb-1">{application.homeAddressLine}</p>
                            )}
                            <p className="mb-0">
                              {[
                                application.homeCity,
                                application.homeState,
                                application.homeZipCode
                              ].filter(Boolean).join(', ')}
                              {application.homeCountry && (
                                <>
                                  {application.homeCity || application.homeState || application.homeZipCode ? ', ' : ''}
                                  {application.homeCountry}
                                </>
                              )}
                            </p>
                          </div>
                        ) : (
                          application.homeAddress && <p className="mb-0">{application.homeAddress}</p>
                        )}
                      </div>
                    )}
                    {application.homeAccommodationType && (
                      <div className="col-md-6 mb-3">
                        <strong>Home Accommodation Type:</strong>
                        <p className="mb-0">{application.homeAccommodationType}</p>
                      </div>
                    )}
                    {(application.studyPhone || application.studyMobile || application.studyFax) && (
                      <div className="col-md-6 mb-3">
                        <strong><Phone size={18} className="me-2" />Study Location Contact:</strong>
                        <p className="mb-0">
                          {application.studyPhone && `Phone: ${application.studyPhone}`}
                          {application.studyPhone && application.studyMobile && ' | '}
                          {application.studyMobile && `Mobile: ${application.studyMobile}`}
                          {application.studyFax && (application.studyPhone || application.studyMobile) && ' | '}
                          {application.studyFax && `Fax: ${application.studyFax}`}
                        </p>
                      </div>
                    )}
                    {application.studyAddress && (
                      <div className="col-12 mb-3">
                        <strong><MapPin size={18} className="me-2" />Study Address:</strong>
                        <p className="mb-0">{application.studyAddress}</p>
                      </div>
                    )}
                    {application.studyAccommodationType && (
                      <div className="col-md-6 mb-3">
                        <strong>Study Accommodation Type:</strong>
                        <p className="mb-0">{application.studyAccommodationType}</p>
                      </div>
                    )}
                    {application.nextOfKinName && (
                      <div className="col-md-6 mb-3">
                        <strong>Next of Kin:</strong>
                        <p className="mb-0">
                          {application.nextOfKinName}
                          {application.nextOfKinEmail && ` (${application.nextOfKinEmail})`}
                        </p>
                        {(application.nextOfKinPhone || application.nextOfKinMobile || application.nextOfKinFax) && (
                          <small className="text-muted d-block mt-1">
                            {application.nextOfKinPhone && `Phone: ${application.nextOfKinPhone}`}
                            {application.nextOfKinPhone && application.nextOfKinMobile && ' | '}
                            {application.nextOfKinMobile && `Mobile: ${application.nextOfKinMobile}`}
                            {application.nextOfKinFax && (application.nextOfKinPhone || application.nextOfKinMobile) && ' | '}
                            {application.nextOfKinFax && `Fax: ${application.nextOfKinFax}`}
                          </small>
                        )}
                        {application.nextOfKinAddress && (
                          <small className="text-muted d-block mt-1">
                            <MapPin size={14} className="me-1" />
                            {application.nextOfKinAddress}
                          </small>
                        )}
                      </div>
                    )}
                    {application.emergencyContactName && (
                      <div className="col-md-6 mb-3">
                        <strong>Emergency Contact:</strong>
                        <p className="mb-0">
                          {application.emergencyContactName}
                          {application.emergencyContactEmail && ` (${application.emergencyContactEmail})`}
                        </p>
                        {(application.emergencyContactPhone || application.emergencyContactMobile) && (
                          <small className="text-muted d-block mt-1">
                            {application.emergencyContactPhone && `Phone: ${application.emergencyContactPhone}`}
                            {application.emergencyContactPhone && application.emergencyContactMobile && ' | '}
                            {application.emergencyContactMobile && `Mobile: ${application.emergencyContactMobile}`}
                          </small>
                        )}
                        {application.emergencyContactAddress && (
                          <small className="text-muted d-block mt-1">
                            <MapPin size={14} className="me-1" />
                            {application.emergencyContactAddress}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* D. Academic Information */}
            {(application.secondarySchoolLocation || application.secondarySchoolName || application.tertiaryInstitutionName || application.englishLevel) && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-semibold"><Award size={18} className="me-2" />D. Academic Information</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {application.secondarySchoolLocation && (
                        <div className="col-md-6 mb-3">
                          <strong>Secondary School Location:</strong>
                          <p className="mb-0">
                            {application.secondarySchoolLocation === 'new-zealand' ? 'New Zealand' : 'Overseas'}
                          </p>
                        </div>
                      )}
                      {application.secondarySchoolName && (
                        <div className="col-md-6 mb-3">
                          <strong>Secondary School:</strong>
                          <p className="mb-0">
                            {application.secondarySchoolName}
                            {application.secondarySchoolLastYear && ` (Last Year: ${application.secondarySchoolLastYear})`}
                          </p>
                          {application.secondarySchoolHighestLevel && (
                            <small className="text-muted">Highest Level: {application.secondarySchoolHighestLevel}</small>
                          )}
                        </div>
                      )}
                      {application.tertiaryInstitutionName && (
                        <div className="col-md-6 mb-3">
                          <strong>Tertiary Institution:</strong>
                          <p className="mb-0">
                            {application.tertiaryInstitutionName}
                            {application.tertiaryInstitutionYear && ` (Year: ${application.tertiaryInstitutionYear})`}
                          </p>
                        </div>
                      )}
                      {application.englishLevel && (
                        <div className="col-md-6 mb-3">
                          <strong>English Level:</strong>
                          <p className="mb-0">{application.englishLevel}</p>
                        </div>
                      )}
                      {application.englishExamType && application.hasEnglishScores && (
                        <div className="col-md-6 mb-3">
                          <strong>English Test ({application.englishExamType}):</strong>
                          <p className="mb-0">
                            Overall: {application.englishOverallScore || 'N/A'}
                            {application.englishExamDate && ` (Date: ${formatDate(application.englishExamDate)})`}
                          </p>
                        </div>
                      )}
                      {application.careerIntentions && (
                        <div className="col-12 mb-3">
                          <strong>Career Intentions:</strong>
                          <p className="mb-0">{application.careerIntentions}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* E. Work History */}
            {(application.workedAsHealthCareAssistant || application.otherHealthcareQualifications) && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-semibold"><Briefcase size={18} className="me-2" />E. Work History</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      {application.workedAsHealthCareAssistant && (
                        <div className="col-md-6 mb-3">
                          <strong>Worked as Health Care Assistant:</strong>
                          <p className="mb-0">
                            Yes
                            {application.healthCareAssistantDuration && ` - ${application.healthCareAssistantDuration}`}
                          </p>
                        </div>
                      )}
                      {application.otherHealthcareQualifications && (
                        <div className="col-md-6 mb-3">
                          <strong>Other Healthcare Qualifications:</strong>
                          <p className="mb-0">
                            Yes
                            {application.healthcareQualificationsDetails && ` - ${application.healthcareQualificationsDetails}`}
                          </p>
                        </div>
                      )}
                      {application.continueStudiesOrWorkforce && (
                        <div className="col-md-6 mb-3">
                          <strong>Continue Studies or Workforce:</strong>
                          <p className="mb-0">{application.continueStudiesOrWorkforce}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* F. Health, Welfare and Accommodation */}
            {(application.insuranceViaSouthernCross ||
              application.insuranceViaUniCare ||
              application.insuranceSelfArranged) && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-semibold">F. Health, Welfare and Accommodation</h5>
                  </div>
                  <div className="card-body">
                    {application.insuranceViaSouthernCross && (
                      <p className="mb-2">
                        <strong>Insurance Preference:</strong> Arrange insurance with Southern Cross through the institution
                      </p>
                    )}
                    {application.insuranceViaUniCare && (
                      <p className="mb-2">
                        <strong>Insurance Preference:</strong> Arrange insurance with Uni-care through the institution
                      </p>
                    )}
                    {application.insuranceSelfArranged && (
                      <p className="mb-0">
                        <strong>Insurance Preference:</strong> Student will organise their own insurance and provide proof prior
                        to arrival
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Additional Information */}
            {application.whyDoYouWantToDoProgramme && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-semibold">Additional Information</h5>
                  </div>
                  <div className="card-body">
                    <strong>Why do you want to do this programme?</strong>
                    <p className="mb-0">{application.whyDoYouWantToDoProgramme}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Documents Checklist */}
            {(Object.keys(documentsMetadata).length > 0 || documents.length > 0) && (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light">
                    <h5 className="mb-0 fw-semibold"><FileText size={18} className="me-2" />Documents Checklist</h5>
                  </div>
                  <div className="card-body">
                    {loadingDocuments ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm text-primary" />
                      </div>
                    ) : (
                      <div className="row">
                        {[
                          { key: 'convictionCheck', label: 'Copy of Conviction Check' },
                          { key: 'passport', label: 'Copy of Passport' },
                          { key: 'visa', label: 'Copy of Visa (if applicable)' },
                          { key: 'transcripts', label: 'Copies of Certified Transcripts' },
                          { key: 'certificates', label: 'Copies of Certificates Awarded' },
                          { key: 'workHistory', label: 'Work History Documents' },
                          { key: 'ieltsToefl', label: 'Evidence of IELTS/PTE Score' },
                        ].map((doc) => {
                          const uploaded = documentsMetadata[doc.key];
                          const file = documents.find((d: any) => d.id === uploaded?.id || String(d.id) === String(uploaded?.id));
                          
                          // Create a file-like object from uploaded metadata if file not found
                          const fileToUse = file || (uploaded?.id ? {
                            id: uploaded.id,
                            originalName: uploaded.fileName,
                            mimeType: getMimeTypeFromFileName(uploaded.fileName || '')
                          } : null);
                          
                          const canPreview = fileToUse && (isImageFile(fileToUse.mimeType) || isPdfFile(fileToUse.mimeType));
                          
                          return (
                            <div key={doc.key} className="col-md-6 mb-3">
                              <div className="d-flex align-items-start justify-content-between" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-start flex-grow-1" style={{ minWidth: 0 }}>
                                  {uploaded ? (
                                    <CheckCircle size={18} className="text-success me-2 mt-1" style={{ flexShrink: 0 }} />
                                  ) : (
                                    <XCircle size={18} className="text-muted me-2 mt-1" style={{ flexShrink: 0 }} />
                                  )}
                                  <div className="flex-grow-1" style={{ minWidth: 0, wordBreak: 'break-word' }}>
                                    <span className={uploaded ? 'text-success' : 'text-muted'}>
                                      {doc.label}
                                    </span>
                                    {uploaded && (
                                      <div className="small text-muted mt-1" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                        {file?.originalName || uploaded.fileName || 'Document uploaded'}
                                        {file?.size && (
                                          <span className="ms-2">
                                            ({(file.size / 1024).toFixed(1)} KB)
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {uploaded && fileToUse && (
                                  <div className="d-flex gap-1 ms-2" style={{ flexShrink: 0 }}>
                                    {canPreview && (
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => handlePreview(fileToUse)}
                                        title="Preview"
                                      >
                                        <Eye size={14} />
                                      </button>
                                    )}
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleDownload(fileToUse.id, fileToUse.originalName || uploaded.fileName)}
                                      title="Download"
                                    >
                                      <Download size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

              </>
            )}
            {/* Additional Documents Section - Shows documents not mapped to specific categories */}
            {documents.length > 0 && (() => {
              // Get all document IDs that are mapped to categories
              const mappedDocumentIds = new Set<number | string>();
              Object.values(documentsMetadata).forEach((meta: any) => {
                if (meta?.id) {
                  // Add ID in all formats to handle type mismatches
                  mappedDocumentIds.add(meta.id);
                  if (typeof meta.id === 'number') {
                    mappedDocumentIds.add(String(meta.id));
                  } else {
                    const numId = parseInt(meta.id, 10);
                    if (!isNaN(numId)) {
                      mappedDocumentIds.add(numId);
                    }
                  }
                }
              });

              // Get all fileIds from institutionUploads (correspondence and admin uploads)
              // These should be shown elsewhere, not in Additional Documents
              const institutionUploadFileIds = new Set<number | string>();
              ((application as any)?.institutionUploads || []).forEach((upload: any) => {
                if (upload?.fileId) {
                  institutionUploadFileIds.add(upload.fileId);
                  if (typeof upload.fileId === 'number') {
                    institutionUploadFileIds.add(String(upload.fileId));
                  } else {
                    const numId = parseInt(upload.fileId, 10);
                    if (!isNaN(numId)) {
                      institutionUploadFileIds.add(numId);
                    }
                  }
                }
              });

              // Filter out documents that are already shown in the checklist or in institutionUploads
              // Also exclude CORRESPONDENCE_UPLOAD documents (they belong elsewhere)
              const additionalDocuments = documents.filter((doc: any) => {
                if (!doc || !doc.id) return false;
                
                const docId = doc.id;
                
                // Exclude if mapped to a checklist category
                if (mappedDocumentIds.has(docId) || 
                    mappedDocumentIds.has(String(docId)) || 
                    mappedDocumentIds.has(Number(docId))) {
                  return false;
                }
                
                // Exclude if it's in institutionUploads (shown elsewhere)
                // This includes both correspondence and admin uploads
                if (institutionUploadFileIds.has(docId) || 
                    institutionUploadFileIds.has(String(docId)) || 
                    institutionUploadFileIds.has(Number(docId))) {
                  return false;
                }
                
                // Exclude CORRESPONDENCE_UPLOAD documents (they belong elsewhere)
                // Check category case-insensitively to be safe
                const docCategory = doc.category?.toUpperCase();
                if (docCategory === 'CORRESPONDENCE_UPLOAD') {
                  return false;
                }
                
                return true;
              });

              if (additionalDocuments.length === 0) return null;

              return (
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light">
                      <h5 className="mb-0 fw-semibold"><FileText size={18} className="me-2" />Additional Documents</h5>
                    </div>
                    <div className="card-body">
                      <div className="application-additional-documents">
                        {additionalDocuments.map((doc: any) => {
                          const canPreview = isImageFile(doc.mimeType) || isPdfFile(doc.mimeType);
                          
                          return (
                            <div key={doc.id} className="application-additional-document">
                                <div className="application-additional-document-info">
                                  <CheckCircle size={18} className="text-success flex-shrink-0 mt-1" />
                                  <div className="application-additional-document-text">
                                    <span className="text-success fw-semibold">
                                      {doc.originalName || doc.fileName || 'Document'}
                                    </span>
                                    {doc.category && (
                                      <div className="small text-muted">
                                        Category: {doc.category}
                                      </div>
                                    )}
                                    {doc.size && (
                                      <div className="small text-muted">
                                        ({(doc.size / 1024).toFixed(1)} KB)
                                      </div>
                                    )}
                                    {doc.createdAt && (
                                      <div className="small text-muted">
                                        Uploaded: {formatDateTime(doc.createdAt)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="application-additional-document-actions">
                                  {canPreview && (
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handlePreview(doc)}
                                      title="Preview" aria-label={`Preview ${doc.originalName || doc.fileName || 'document'}`}
                                    >
                                      <Eye size={14} />
                                    </button>
                                  )}
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleDownload(doc.id, doc.originalName || doc.fileName || 'document')}
                                    title="Download" aria-label={`Download ${doc.originalName || doc.fileName || 'document'}`}
                                  >
                                    <Download size={14} />
                                  </button>
                                </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Application Metadata */}
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h5 className="mb-0 fw-semibold">Application Information</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <strong>Created:</strong>
                      <p className="mb-0">{formatDateTime(application.createdAt)}</p>
                    </div>
                    {application.submittedAt && (
                      <div className="col-md-6 mb-3">
                        <strong>Submitted:</strong>
                        <p className="mb-0">{formatDateTime(application.submittedAt)}</p>
                      </div>
                    )}
                    <div className="col-md-6 mb-3">
                      <strong>Last Updated:</strong>
                      <p className="mb-0">{formatDateTime(application.updatedAt)}</p>
                    </div>
                    {application.notes && (
                      <div className="col-12 mb-3">
                        <strong>Notes:</strong>
                        {(() => {
                          // Notes may arrive as a serialized array or legacy plain text.
                          let notesArray: unknown = application.notes;
                          if (typeof notesArray === 'string') {
                            try {
                              const parsed: unknown = JSON.parse(notesArray);
                              if (Array.isArray(parsed)) {
                                notesArray = parsed;
                              } else {
                                return <p className="mb-0">{application.notes}</p>;
                              }
                            } catch {
                              return <p className="mb-0">{application.notes}</p>;
                            }
                          }
                          if (Array.isArray(notesArray) && notesArray.length > 0) {
                            return (
                              <div className="mt-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {notesArray.map((noteEntry: any, index: number) => {
                                  // Handle legacy string format in array
                                  if (typeof noteEntry === 'string') {
                                    return (
                                      <div key={index} className="mb-2 pb-2 border-bottom">
                                        <p className="mb-0">{noteEntry}</p>
                                        <small className="text-muted">Legacy note</small>
                                      </div>
                                    );
                                  }
                                  if (!noteEntry || typeof noteEntry !== 'object' || typeof noteEntry.note !== 'string') {
                                    return null;
                                  }
                                  
                                  // Handle new format with timestamp
                                  const attachmentRefs = [...(Array.isArray(noteEntry.attachments) ? noteEntry.attachments : []),
                      ...(noteEntry.documentId ? [{ documentId: noteEntry.documentId, documentCategory: noteEntry.documentCategory }] : [])]
                      .filter((attachment: any, index: number, all: any[]) => attachment?.documentId && all.findIndex(other => String(other?.documentId) === String(attachment.documentId)) === index);
                                  
                                  // Format document category for display
                                  const formatDocumentCategory = (category: string | null | undefined): string => {
                                    if (!category) return '';
                                    const categoryMap: Record<string, string> = {
                                      'offerLetter': 'Offer Letter',
                                      'visaApplicationForm': 'Visa Application Form',
                                    };
                                    return categoryMap[category] || category;
                                  };
                                  
                                  // Determine portal: use portal field if available, otherwise infer from type field
                                  const notePortal = noteEntry.portal || (noteEntry.type === 'AGENT_RESPONSE' ? 'agent' : 'admin');
                                  const portalColor = notePortal === 'agent' 
                                    ? (settings?.agentPortalPrimaryColor || '#0C6AB2')
                                    : (settings?.adminPortalPrimaryColor || '#C6339D');
                                  
                                  return (
                                    <div 
                                      key={index} 
                                      className="mb-2 pb-2 border-bottom"
                                      style={{
                                        borderLeft: `4px solid ${portalColor}`,
                                        paddingLeft: '12px',
                                        marginLeft: '0'
                                      }}
                                    >
                                      <p className="mb-0">{noteEntry.note}</p>
                                      {attachmentRefs.map((attachment: any) => {
                          const noteDocument = documents.find((document: any) => String(document.id) === String(attachment.documentId));
                          if (!noteDocument) return <p key={attachment.documentId} className="text-muted mt-2">Attachment unavailable (#{attachment.documentId})</p>;
                          const canPreviewNoteDoc = isImageFile(noteDocument.mimeType) || isPdfFile(noteDocument.mimeType);
                          return (
                                        <div key={attachment.documentId} className="mt-3 p-3 bg-light rounded">
                                          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                            <div className="flex-grow-1" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                              <div className="d-flex align-items-center mb-1">
                                                <FileText size={18} className="text-primary me-2" />
                                                <div>
                                                  <strong className="d-block">{noteDocument.originalName || noteDocument.fileName || 'Document'}</strong>
                                                  {attachment.documentCategory && (
                                                    <small className="text-muted">Category: {formatDocumentCategory(attachment.documentCategory)}</small>
                                                  )}
                                                  {noteDocument.size && (
                                                    <small className="text-muted ms-2">
                                                      ({(noteDocument.size / 1024).toFixed(1)} KB)
                                                    </small>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="d-flex gap-1 ms-2">
                                              {canPreviewNoteDoc && (
                                                <button
                                                  className="btn btn-sm btn-outline-primary"
                                                  onClick={() => handlePreview(noteDocument)}
                                                  title="Preview" aria-label={`Preview ${noteDocument.originalName || noteDocument.fileName || 'document'}`}
                                                >
                                                  <Eye size={14} />
                                                </button>
                                              )}
                                              <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => handleDownload(noteDocument.id, noteDocument.originalName || noteDocument.fileName || 'document')}
                                                title="Download" aria-label={`Download ${noteDocument.originalName || noteDocument.fileName || 'document'}`}
                                              >
                                                <Download size={14} />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      );
                          })}
                                      <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="text-muted">
                                          Added by <strong>{noteEntry.addedByName || 'Unknown'}</strong>
                                        </small>
                                        <small className="text-muted">
                                          {formatDateTime(noteEntry.timestamp)}
                                        </small>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          }
                          
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Agent Message Section */}
          <div className={`alert ${application.requiresResponse ? 'alert-warning' : 'alert-primary'} border-0 shadow-sm mb-4`}>
            <div className="d-flex align-items-start">
              <div className="flex-grow-1">
                <h5 className="alert-heading mb-3">
                  <FileText size={20} className="me-2" />
                  {application.requiresResponse ? 'Response Required' : 'Send Message'}
                </h5>
                <p className="mb-3">
                  {application.requiresResponse
                    ? 'This application requires a response from you. Please provide your response below.'
                    : 'Use this to start a new note/message thread with admin for this application.'}
                </p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    {application.requiresResponse ? 'Your Response' : 'Your Message'}
                  </label>
                  <textarea
                    className="form-control"
                    rows={5}
                    value={agentResponse}
                    onChange={(e) => setAgentResponse(e.target.value)}
                    placeholder={
                      application.requiresResponse
                        ? 'Enter your response to the status change...'
                        : 'Write a note/message for admin...'
                    }
                    disabled={submittingResponse}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSendMessage}
                  disabled={submittingResponse || !agentResponse.trim()}
                >
                  {submittingResponse ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} className="me-2" />
                      {application.requiresResponse ? 'Submit Response' : 'Send Message'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <DocumentRequestList onPreview={request => { const file = request.document || documents.find(d => d.id === request.documentId); if (file) void handlePreview(file); }} notes={application.notes} requests={application.documentRequests || []} onDownload={r => { if(r.documentId) void handleDownload(r.documentId,r.document?.originalName || r.document?.fileName || r.label); }} onUpload={async (r,file,operationId) => {
            await agentApi.uploadDocument(file,r.category,application.id,r.id,operationId);
            await loadApplication();
            const refreshed = await agentApi.getApplicationDocuments(application.id);
            setDocuments(refreshed);
            toast.success('Requested document supplied');
          }} />
          {application.requiresDocumentUpload && !application.documentRequests?.length && <div className="alert alert-info">A document has been requested. Refresh to load its individual upload action.</div>}

          {/* Action Buttons */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => navigate('/applications')}
                >
                  <ArrowLeft size={18} className="me-2" />
                  Back to Applications
                </button>
                {application.status === 'DRAFT' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/applications/${application.id}/edit`)}
                  >
                    <FileText size={18} className="me-2" />
                    Continue Editing
                  </button>
                )}
              </div>
            </div>
          </div>
            </section>
          </div>
      </div>

      {/* Document Preview Modal - Rendered via Portal */}
      {previewingDocument && previewUrl && createPortal(
        <div 
          className="modal fade show d-block" 
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050
          }} 
          onClick={closePreview}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
        >
          <div 
            className="modal-dialog modal-xl modal-dialog-centered" 
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 1051 }}
          >
            <div className="modal-content shadow-lg">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-semibold">{previewingDocument.originalName}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closePreview}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-0" style={{ maxHeight: '80vh', overflow: 'auto' }}>
                {isImageFile(previewingDocument.mimeType) ? (
                  <div className="d-flex justify-content-center align-items-center p-4" style={{ minHeight: '400px' }}>
                    <img 
                      src={previewUrl} 
                      alt={previewingDocument.originalName} 
                      className="img-fluid rounded shadow-sm"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '75vh', 
                        height: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                ) : isPdfFile(previewingDocument.mimeType) ? (
                  <div style={{ height: '75vh' }}>
                    <iframe
                      src={previewUrl}
                      title={previewingDocument.originalName}
                      className="w-100 h-100"
                      style={{ border: 'none' }}
                    />
                  </div>
                ) : (
                  <div className="p-5 text-center">
                    <FileText size={64} className="text-muted mb-3" />
                    <h6 className="mb-2">{previewingDocument.originalName}</h6>
                    <p className="text-muted mb-4">Preview not available for this file type</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleDownload(previewingDocument.id, previewingDocument.originalName)}
                    >
                      <Download size={18} className="me-2" />
                      Download File
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top">
                <button
                  className="btn btn-primary"
                  onClick={() => handleDownload(previewingDocument.id, previewingDocument.originalName)}
                >
                  <Download size={18} className="me-2" />
                  Download
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closePreview}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
